import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";

import { Button, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";

import { ApiReportTemplateStatusChoices, ReportTemplateType } from "@api/graphql/types-and-hooks";
import { ReportTemplate } from "@components/TemplatesTable/TemplatesTable";
import { ConfirmArchiveDialog } from "@pages/Templates/components/Dialog/ConfirmArchiveDialog";
import { ConfirmPublishDialog } from "@pages/Templates/components/Dialog/ConfirmPublishDialog";
import { useReportTemplateActions } from "@pages/Templates/hooks/templates";

interface TemplateEditorFooterButtonRowProps {
  template: ReportTemplateType;
  onSave: () => void;
  refetch: () => void;
  disablePublishButton: boolean;
  isSaving?: boolean;
}

export const TemplateEditorFooterButtonRow: React.FC<TemplateEditorFooterButtonRowProps> = ({
  template,
  onSave,
  refetch,
  disablePublishButton,
  isSaving = false,
}) => {
  const status = template?.status;
  const lastUpdated = new Date(template.lastUpdatedDate);
  const formattedDate = format(lastUpdated, "MMM d, yyyy 'at' h:mm a");

  const isDraft = status === ApiReportTemplateStatusChoices.Draft;
  const isPublished = status === ApiReportTemplateStatusChoices.Published;

  const canArchive = !template.isBaseTemplate;

  const {
    archiveDialogOpen,
    setArchiveDialogOpen,
    publishDialogOpen,
    setPublishDialogOpen,
    publishTemplate,
    archiveTemplate,
    setSelectedTemplate,
  } = useReportTemplateActions({ refetch: refetch });

  const setTemplate = () => {
    setSelectedTemplate({ id: template.id, name: template.name } as ReportTemplate);
  };

  const confirmArchive = () => {
    setTemplate();
    setArchiveDialogOpen(true);
  };

  const confirmPublish = () => {
    setTemplate();
    setPublishDialogOpen(true);
  };

  return (
    <div
      id="btnRow"
      className={`-ml-12 flex w-full items-center justify-end border-t border-limestone-100 px-8 fixed bottom-0  z-10 bg-white py-[11.5px]`}
    >
      <div className="flex items-center gap-2">
        <Paragraph size="small" colorType="secondary">
          <span>
            Last Updated: {formattedDate}
            {template.updatedByDisplay && (
              <>
                {" "}
                by <strong>{template.updatedByDisplay}</strong>
              </>
            )}
          </span>
        </Paragraph>
        {isDraft && (
          <Button
            appearance="secondary"
            disabled={isSaving}
            onClick={onSave}
            text={
              isSaving ? (
                <span className="inline-flex items-center gap-1.5">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                "Save Draft"
              )
            }
          />
        )}
        {isDraft && (
          <Tooltip
            showInfoIcon={false}
            disabled={!disablePublishButton}
            text="Please save your template before publishing"
          >
            <Button appearance="primary" text="Publish" disabled={disablePublishButton} onClick={confirmPublish} />
          </Tooltip>
        )}
        {canArchive && isPublished && <Button appearance="primary" text="Archive" onClick={confirmArchive} />}
      </div>

      {isDraft && (
        <ConfirmPublishDialog
          open={publishDialogOpen}
          setOpen={setPublishDialogOpen}
          onConfirm={publishTemplate}
          onCancel={() => setPublishDialogOpen(false)}
          templateName={template.name}
        />
      )}
      {isPublished && (
        <ConfirmArchiveDialog
          open={archiveDialogOpen}
          setOpen={setArchiveDialogOpen}
          onConfirm={archiveTemplate}
          onCancel={() => setArchiveDialogOpen(false)}
          templateName={template.name}
        />
      )}
    </div>
  );
};
